import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
  DialogActions,
  Tooltip,
  Chip,
  Stack,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";
import FilterInvoice from "./InvoiceForm/FilterInvoice";
import InvoiceDocuments from "./InvoiceForm//InvoiceDocuments";
import xmlIcon from "../../static/media/icon/xml.svg";

const getCookie = (name) => {
  const v = `; ${document.cookie}`;
  const parts = v.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) => `$${toNum(v).toFixed(2)}`;

const norm = (v) => String(v || "").trim().toUpperCase();

const cellHeadSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const compactTextSx = {
  fontSize: 13,
  lineHeight: 1.2,
};

const AdminInvoice = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");

  const [origenFilter, setOrigenFilter] = useState("TODOS");
  const [tipoDocumentoFilter, setTipoDocumentoFilter] = useState("TODOS");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showAnulados, setShowAnulados] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [excelAnchor, setExcelAnchor] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const [loadingModal, setLoadingModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmAnular, setConfirmAnular] = useState({
    open: false,
    doc: null,
  });

  const [downloadModal, setDownloadModal] = useState({
    open: false,
    title: "Descargando reporte",
    message: "Generando el documento…",
  });

  const fetchDocs = async () => {
    try {
      const r = await fetch("/facturas/documentos/listar/", { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      setDocs(d.documentos || []);
    } catch {
      setDocs([]);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search, origenFilter, tipoDocumentoFilter, dateFrom, dateTo, showAnulados]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  const isVenta = (d) => norm(d?.origen) === "VENTA";
  const isCompra = (d) => norm(d?.origen) === "COMPRA";

  const isDraft = (d) => {
    const e = norm(d?.estado);
    const s = norm(d?.sri_estado);
    return e === "BORRADOR" || e === "DRAFT" || e === "PREFAC" || e === "PREFACTURA" || s === "BORRADOR";
  };

  const isAuthorized = (d) => {
    const s = norm(d?.sri_estado);
    const e = norm(d?.estado);
    return s === "AUTORIZADO" || (isVenta(d) && e === "AUTORIZADO");
  };

  const isNoAutorizado = (d) => {
    const s = norm(d?.sri_estado);
    const e = norm(d?.estado);
    return s === "NO AUTORIZADO" || s === "NO_AUTORIZADO" || (isVenta(d) && (e === "NO AUTORIZADO" || e === "NO_AUTORIZADO"));
  };

  const isPendienteSri = (d) => {
    const s = norm(d?.sri_estado);
    const e = norm(d?.estado);
    return (
      s === "EN PROCESAMIENTO" ||
      s === "PENDIENTE" ||
      s === "PENDIENTE_SRI" ||
      e === "PENDIENTE_SRI" ||
      e === "XML_GENERADO" ||
      e === "ENVIADO" ||
      e === "ERROR"
    );
  };

  const isAnulada = (estado) => {
    const e = norm(estado);
    return e === "ANULADA" || e === "ANULADO";
  };

  const netoDoc = (d) => {
    if (d.neto !== undefined) return d.neto;
    const s15 = toNum(d.subtotal_15);
    const s5 = toNum(d.subtotal_5);
    const s0 = toNum(d.subtotal_0);
    const desc = toNum(d.descuento_total);
    const base = s15 + s5 + s0;
    return base - desc;
  };

  const impDoc = (d) => {
    if (d.impuesto !== undefined) return d.impuesto;
    const i15 = toNum(d.iva_15);
    const i5 = toNum(d.iva_5);
    const iva = toNum(d.iva);
    return i15 + i5 + iva;
  };

  const retDoc = (d) => {
    if (d.retenciones_total !== undefined) return toNum(d.retenciones_total);
    if (d.retencion_total !== undefined) return toNum(d.retencion_total);
    if (d.retenciones !== undefined) return toNum(d.retenciones);
    return 0;
  };

  const documentoPrefix = (d) => {
    if (isCompra(d)) return "COM";
    if (isDraft(d)) return "PREFACTURA";
    if (d.tipo_documento === "04") return "NC";
    if (d.tipo_documento === "05") return "ND";
    if (d.tipo_documento === "06") return "GR";
    if (d.tipo_documento === "07") return "RET";
    return "FAC";
  };

  const documentoLabel = (d) => {
    if (isCompra(d)) return "Compra";
    if (isDraft(d)) return "Prefactura";
    if (d.tipo_documento === "04") return "Nota de crédito";
    if (d.tipo_documento === "05") return "Nota de débito";
    if (d.tipo_documento === "06") return "Guía de remisión";
    if (d.tipo_documento === "07") return "Retención";
    return "Factura";
  };

  const documentoRoute = (d) => {
    if (isCompra(d)) return `/admin-panel/compra/${d.id}`;
    if (d.tipo_documento === "04") return `/admin-panel/nota-credito/${d.id}`;
    if (d.tipo_documento === "05") return `/admin-panel/nota-debito/${d.id}`;
    if (d.tipo_documento === "06") return `/admin-panel/guia-remision/${d.id}`;
    if (d.tipo_documento === "07") return `/admin-panel/retencion/${d.id}`;
    return `/admin-panel/factura/${d.id}`;
  };

  const rolDocumento = (d) => (isCompra(d) ? "Proveedor" : "Cliente");

  const openMenu = (e, d) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDoc(d);
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  const openExcelMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExcelAnchor(e.currentTarget);
  };

  const closeExcelMenu = () => {
    setExcelAnchor(null);
  };

  const anularCompra = async () => {
    if (!selectedDoc) return;

    try {
      const csrftoken = getCookie("csrftoken");
      const compraId = selectedDoc.document_id;

      setLoadingModal({
        open: true,
        title: "Anulando documento",
        message: "Procesando anulación…",
      });

      const r = await fetch("/documentos/compras/anular/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ compra_id: compraId }),
      });

      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Respuesta no válida del servidor");

      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo anular");

      setDocs((prev) =>
        prev.map((x) =>
          x.origen === "COMPRA" && x.document_id === selectedDoc.document_id
            ? { ...x, estado: "ANULADA", sri_estado: "ANULADA", inventario_aplicado: false }
            : x
        )
      );

      setSnackbar({
        open: true,
        message: "Documento anulado correctamente",
        type: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error al anular",
        type: "error",
      });
    } finally {
      setLoadingModal({ open: false, title: "", message: "" });
      closeMenu();
    }
  };

  const reenviarSri = async () => {
    if (!selectedDoc) return;

    try {
      const csrftoken = getCookie("csrftoken");

      setLoadingModal({
        open: true,
        title: "Consultando estado del documento",
        message: "Verificando autorización y reenviando si hace falta…",
      });

      const r = await fetch("/facturas/documentos/reenviar/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ documento_id: selectedDoc.id }),
      });

      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Respuesta no válida del servidor");

      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo procesar el documento");

      setDocs((prev) =>
        prev.map((x) =>
          x.id === selectedDoc.id
            ? {
                ...x,
                estado: data.estado || x.estado,
                sri_estado: data.sri_estado || x.sri_estado,
              }
            : x
        )
      );

      const sri = norm(data.sri_estado || data.estado);

      setSnackbar({
        open: true,
        message:
          sri === "AUTORIZADO"
            ? "Documento autorizado correctamente"
            : sri === "NO AUTORIZADO" || sri === "NO_AUTORIZADO"
            ? "El SRI no autorizó el documento"
            : "El documento fue consultado correctamente",
        type:
          sri === "AUTORIZADO"
            ? "success"
            : sri === "NO AUTORIZADO" || sri === "NO_AUTORIZADO"
            ? "warning"
            : "info",
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error al consultar", type: "error" });
    } finally {
      setLoadingModal({ open: false, title: "", message: "" });
      closeMenu();
    }
  };

  const anularFactura = async (docToCancel) => {
    if (!docToCancel) return;

    try {
      const csrftoken = getCookie("csrftoken");

      setLoadingModal({
        open: true,
        title: "Anulando documento",
        message: "Revirtiendo stock y actualizando estado…",
      });

      const r = await fetch("/facturas/documentos/anular/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken,
        },
        body: JSON.stringify({ documento_id: docToCancel.id }),
      });

      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Respuesta no válida del servidor");

      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || "No se pudo anular el documento");

      setDocs((prev) =>
        prev.map((x) =>
          x.id === docToCancel.id ? { ...x, estado: "ANULADA", sri_estado: "ANULADA" } : x
        )
      );

      setSnackbar({ open: true, message: "Documento anulado correctamente", type: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error al anular documento", type: "error" });
    } finally {
      setLoadingModal({ open: false, title: "", message: "" });
      setConfirmAnular({ open: false, doc: null });
      closeMenu();
    }
  };

  const handleModifyDraft = async () => {
    if (!selectedDoc) return;

    try {
      const r = await fetch(`/facturas/documentos/modificar/${selectedDoc.id}/`, {
        credentials: "include",
      });

      const data = await r.json();

      if (!r.ok || !data.ok) {
        throw new Error(data.error || "No se pudo cargar la prefactura");
      }

      navigate("/admin-panel/registro/documento", {
        state: {
          prefill_invoice: data,
        },
      });

      closeMenu();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error al cargar la prefactura",
        type: "error",
      });
    }
  };

  const startDownload = async (mode) => {
    const { ok, error } = InvoiceDocuments.validateRangeRequired({
      dateFrom,
      dateTo,
    });

    if (!ok) {
      setSnackbar({ open: true, message: error, type: "warning" });
      return;
    }

    setDownloadModal({ open: true, title: "Descargando reporte", message: "Generando el documento…" });

    const params = new URLSearchParams({
      origen: origenFilter,
      tipo_documento: tipoDocumentoFilter,
      search,
      date_from: dateFrom,
      date_to: dateTo,
      include_anulados: showAnulados ? "1" : "0",
    });

    const url =
      mode === "DETALLADO"
        ? `/facturas/documentos/excel/detallado/?${params.toString()}`
        : `/facturas/documentos/excel/?${params.toString()}`;

    try {
      await InvoiceDocuments.downloadFile(url);
      await InvoiceDocuments.delay(2500);
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || "Error al descargar", type: "error" });
    } finally {
      setDownloadModal((s) => ({ ...s, open: false }));
      closeExcelMenu();
    }
  };

  const puedeAcciones = (d) => {
    if (isAnulada(d.estado)) return false;
    if (isCompra(d)) return true;
    if (isVenta(d)) return true;
    return false;
  };

  const filtered = useMemo(() => {
    const t = search.toLowerCase().trim();

    return (docs || []).filter((d) => {
      const origen = String(d.origen || "").toUpperCase();

      let okOrigen = true;

      switch (String(origenFilter || "TODOS").toUpperCase()) {
        case "VENTA":
          okOrigen = origen === "VENTA";
          break;
        case "COMPRA":
          okOrigen = origen === "COMPRA";
          break;
        default:
          okOrigen = true;
      }

      const okTipoDocumento =
        String(tipoDocumentoFilter || "TODOS").toUpperCase() === "TODOS" ||
        String(d.tipo_documento || "") === String(tipoDocumentoFilter || "");

      const okAnulados = showAnulados ? true : !isAnulada(d.estado);

      const okSearch =
        !t ||
        String(d.persona || "").toLowerCase().includes(t) ||
        String(d.numero_documento || "").toLowerCase().includes(t) ||
        String(d.codigo || "").toLowerCase().includes(t) ||
        String(d.sri_estado || "").toLowerCase().includes(t) ||
        String(d.estado || "").toLowerCase().includes(t) ||
        String(d.tipo_documento_label || "").toLowerCase().includes(t) ||
        String(d.sri_autorizacion || "").toLowerCase().includes(t) ||
        String(d.clave_acceso || "").toLowerCase().includes(t);

      const fecha = String(d.fecha_emision || "");
      const okDateFrom = !dateFrom || (fecha && fecha >= dateFrom);
      const okDateTo = !dateTo || (fecha && fecha <= dateTo);

      return okOrigen && okTipoDocumento && okAnulados && okSearch && okDateFrom && okDateTo;
    });
  }, [docs, search, origenFilter, tipoDocumentoFilter, dateFrom, dateTo, showAnulados]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: "0 10px 30px rgba(15,23,42,0.06)" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Documentos
                </Typography>
              </Box>

              <Tooltip title="Descargar reporte" arrow>
                <IconButton
                  onClick={openExcelMenu}
                  sx={{
                    p: 0.7,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  <img src={xmlIcon} alt="Descargar reporte" style={{ width: 26, height: 26 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <FilterInvoice
              search={search}
              setSearch={setSearch}
              origenFilter={origenFilter}
              setOrigenFilter={setOrigenFilter}
              tipoDocumentoFilter={tipoDocumentoFilter}
              setTipoDocumentoFilter={setTipoDocumentoFilter}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              showAnulados={showAnulados}
              setShowAnulados={setShowAnulados}
            />

            <TableContainer
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "100%",
                "&::-webkit-scrollbar": { height: 10 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: 10,
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 1000, tableLayout: "auto" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 92 }}>Emisión</TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 200 }}>Persona</TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 190 }}>Documento</TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 150 }} align="center">
                      Estado / Autoriz. SRI
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 90 }} align="right">
                      Neto
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 90 }} align="right">
                      Imp.
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 90 }} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 90 }} align="right">
                      Ret.
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 90 }} align="right">
                      Saldo
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, minWidth: 82 }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((d) => (
                      <TableRow
                        key={d.id}
                        hover
                        sx={{
                          "& td": { py: 1, borderBottom: "1px solid #edf2f7" },
                          "&:hover": { bgcolor: "#fafcff" },
                        }}
                      >
                        <TableCell sx={{ minWidth: 92 }}>
                          <Typography sx={{ ...compactTextSx, fontWeight: 600, color: "#0f172a" }}>
                            {d.fecha_emision}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography sx={{ ...compactTextSx, fontWeight: 600, color: "#0f172a" }}>
                            {d.persona}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.35 }}>
                            <PersonOutlineIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 500 }}>
                              {rolDocumento(d)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ minWidth: 190 }}>
                          <Link
                            href={documentoRoute(d)}
                            target="_blank"
                            underline="hover"
                            sx={{
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.6,
                              color: "#1565C0",
                              fontSize: 13,
                              lineHeight: 1.2,
                            }}
                          >
                            {documentoPrefix(d)} {d.numero_documento}
                          </Link>
                          <Typography sx={{ fontSize: 10.5, color: "#64748b", mt: 0.35 }}>
                            {documentoLabel(d)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ minWidth: 150 }} align="center">
                          {isAnulada(d.estado) ? (
                            <Chip
                              icon={<CancelIcon sx={{ fontSize: 14 }} />}
                              label="Anulada"
                              color="error"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                height: 24,
                                "& .MuiChip-label": { px: 1, fontSize: 11 },
                              }}
                            />
                          ) : isCompra(d) ? (
                            <Chip
                              icon={<Inventory2OutlinedIcon sx={{ fontSize: 14 }} />}
                              label="Registrada"
                              color="success"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                height: 24,
                                "& .MuiChip-label": { px: 1, fontSize: 11 },
                              }}
                            />
                          ) : isDraft(d) ? (
                            <Chip
                              icon={<EditNoteOutlinedIcon sx={{ fontSize: 14 }} />}
                              label="Prefactura"
                              color="warning"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                height: 24,
                                "& .MuiChip-label": { px: 1, fontSize: 11 },
                              }}
                            />
                          ) : isAuthorized(d) ? (
                            <Chip
                              icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />}
                              label="Autorizada"
                              color="success"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                height: 24,
                                "& .MuiChip-label": { px: 1, fontSize: 11 },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<AutorenewRoundedIcon sx={{ fontSize: 14 }} />}
                              label="Emitida"
                              color="info"
                              variant="outlined"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                height: 24,
                                "& .MuiChip-label": { px: 1, fontSize: 11 },
                              }}
                            />
                          )}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
                          {money(netoDoc(d))}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
                          {money(impDoc(d))}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>
                          {money(d.total)}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>
                          {money(retDoc(d))}
                        </TableCell>

                        <TableCell align="right" sx={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>
                          {money(d.saldo)}
                        </TableCell>

                        <TableCell align="center" sx={{ minWidth: 82 }}>
                          {puedeAcciones(d) && (
                            <IconButton
                              size="small"
                              onClick={(e) => openMenu(e, d)}
                              sx={{
                                color: "#1565C0",
                                borderRadius: 2,
                                border: "1px solid #dbeafe",
                                bgcolor: "#f8fbff",
                                "&:hover": { bgcolor: "#eef6ff" },
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        <Stack spacing={0.75} alignItems="center">
                          <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                            No hay documentos para mostrar
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            Ajusta los filtros o cambia el rango de fechas
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Menu
        anchorEl={excelAnchor}
        open={Boolean(excelAnchor)}
        onClose={closeExcelMenu}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startDownload("RESUMEN");
          }}
          sx={{ fontWeight: 600 }}
        >
          Documento Excel
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startDownload("DETALLADO");
          }}
          sx={{ fontWeight: 600 }}
        >
          Documento Excel detallado
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {selectedDoc?.origen === "VENTA" && isDraft(selectedDoc) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleModifyDraft();
            }}
            sx={{ fontWeight: 600 }}
          >
            Modificar prefactura
          </MenuItem>
        )}

        {selectedDoc?.origen === "VENTA" && !isDraft(selectedDoc) && !isAnulada(selectedDoc?.estado) && !isAuthorized(selectedDoc) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              reenviarSri();
            }}
            sx={{ fontWeight: 600 }}
          >
            Consultar / reenviar SRI
          </MenuItem>
        )}

        {selectedDoc?.origen === "VENTA" && !isAnulada(selectedDoc?.estado) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setConfirmAnular({ open: true, doc: selectedDoc });
            }}
            sx={{ fontWeight: 600, color: "#d32f2f" }}
          >
            Anular documento
          </MenuItem>
        )}

        {selectedDoc?.origen === "COMPRA" && !isAnulada(selectedDoc?.estado) && (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              anularCompra();
            }}
            sx={{ fontWeight: 600 }}
          >
            Anular documento
          </MenuItem>
        )}
      </Menu>

      <Dialog open={loadingModal.open} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{loadingModal.title}</DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography sx={{ fontWeight: 600 }}>{loadingModal.message}</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={downloadModal.open} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{downloadModal.title}</DialogTitle>
        <DialogContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography sx={{ fontWeight: 600 }}>{downloadModal.message}</Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmAnular.open}
        onClose={() => setConfirmAnular({ open: false, doc: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#d32f2f" }}>Confirmar anulación</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            ¿Seguro deseas anular el documento <b>{confirmAnular.doc?.numero_documento || ""}</b>?
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 12, color: "#757575" }}>
            Esto marcará el documento como anulado y revertirá inventario según tu configuración.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAnular({ open: false, doc: null })} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={() => anularFactura(confirmAnular.doc)}
            variant="contained"
            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" } }}
          >
            Anular
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminInvoice;